import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Users, Leaf } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Impact() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

 const indicators = [
 { value: "4", label: l("Maisons", "Houses", "Casas"), detail: l("Énergies renouvelables · Réfrigération & Climatisation · Matières premières & Cosmétiques · Agroalimentaire.", "Renewable energy · Refrigeration & AC · Raw materials & Cosmetics · Food & Terroir.", "Energias renováveis · Refrigeração & Climatização · Matérias-primas & Cosméticos · Agroalimentar.") },
 { value: "10", label: l("Trésors valorisés", "Treasures celebrated", "Tesouros valorizados"), detail: l("Ingrédients d'exception intégrés à nos collections.", "Exceptional ingredients integrated into our collections.", "Ingredientes de excepção integrados nas nossas colecções.") },
 { value: "8", label: l("Secteurs d'activité", "Activity sectors", "Sectores de actividade"), detail: l("De l'énergie solaire à l'art de vivre africain.", "From solar energy to African lifestyle.", "Da energia solar à arte de viver africana.") },
 { value: "2023", label: l("Année de fondation", "Year of foundation", "Ano de fundação"), detail: l("Un projet familial pensé sur le temps long.", "A family project designed for the long term.", "Um projecto familiar pensado a longo prazo.") },
 { value: "3", label: l("Langues officielles", "Official languages", "Línguas oficiais"), detail: l("Français, portugais, anglais, pour l'ancrage régional et l'ouverture internationale.", "French, Portuguese, English, for regional roots and international reach.", "Francês, português, inglês, para o enraizamento regional e a abertura internacional.") },
 ];

 // Étape 6. Trois axes d'impact structurés
 const axes = [
 {
 icon: TrendingUp,
 label: l("Impact économique", "Economic impact", "Impacto económico"),
 title: l("Créer de la valeur en Afrique.", "Creating value in Africa.", "Criar valor em África."),
 points: [
 l("Transformation locale des matières premières (anacarde, karité, plantes)", "Local processing of raw materials (cashew, shea, plants)", "Transformação local das matérias-primas (caju, karité, plantas)"),
 l("Circuits courts et rémunération équitable des producteurs", "Short supply chains and fair remuneration of producers", "Circuitos curtos e remuneração justa dos produtores"),
 l("Export international : valeur ajoutée qui reste sur le continent", "International export: added value that stays on the continent", "Exportação internacional: valor acrescentado que fica no continente"),
 l("Modèle diversifié résilient : 5 Maisons, 8 secteurs", "Resilient diversified model: 5 Houses, 8 sectors", "Modelo diversificado resiliente: 5 Casas, 8 sectores"),
 ],
 },
 {
 icon: Users,
 label: l("Impact social", "Social impact", "Impacto social"),
 title: l("Grandir avec nos territoires.", "Growing with our territories.", "Crescer com os nossos territórios."),
 points: [
 l("Emplois créés en Guinée-Bissau", "Jobs created in Guinea-Bissau", "Empregos criados na Guiné-Bissau"),
 l("Coopératives féminines soutenues (karité, sel, transformation)", "Women's cooperatives supported (shea, salt, processing)", "Cooperativas femininas apoiadas (karité, sal, transformação)"),
 l("Formation des jeunes talents via l'Académie NANDA SÁ", "Training of young talents via the NANDA SÁ Académie", "Formação de jovens talentos através da Académie NANDA SÁ"),
 l("Transmission des savoir-faire ouest-africains aux générations futures", "Transmission of West African know-how to future generations", "Transmissão dos saberes oeste-africanos às gerações futuras"),
 ],
 },
 {
 icon: Leaf,
 label: l("Impact environnemental", "Environmental impact", "Impacto ambiental"),
 title: l("Un développement responsable.", "Responsible development.", "Um desenvolvimento responsável."),
 points: [
 l("Énergie solaire : centrales déployées pour entreprises et écoles", "Solar energy: plants deployed for businesses and schools", "Energia solar: centrais implantadas para empresas e escolas"),
 l("Filières agro-forestières respectueuses (baobab, moringa, hibiscus)", "Respectful agro-forestry sectors (baobab, moringa, hibiscus)", "Fileiras agro-florestais respeitosas (baobá, moringa, hibisco)"),
 l("Packaging minimal, formulations courtes chez NANDA SÁ Cosmétiques", "Minimal packaging, short formulations at NANDA SÁ Cosmétiques", "Embalagem mínima, formulações curtas na NANDA SÁ Cosmétiques"),
 l("Empreinte carbone limitée par la production locale", "Limited carbon footprint through local production", "Pegada carbónica limitada pela produção local"),
 ],
 },
 ];

 return (
 <div data-testid="page-impact">
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <Breadcrumbs items={[{ label: l("Impact", "Impact", "Impacto") }]} className="mb-6" />
 <div className="label-overline text-nanda-muted">{l("Impact", "Impact", "Impacto")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("L'empreinte du Groupe, en chiffres.", "The Group's footprint, in figures.", "A pegada do Grupo, em números.")}
 </h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "Nous partageons ici les données que nous pouvons attester. Les indicateurs complémentaires seront publiés au fil de nos bilans annuels.",
 "We share here the data we can attest to. Additional indicators will be published throughout our annual reports.",
 "Partilhamos aqui os dados que podemos atestar. Os indicadores complementares serão publicados ao longo dos nossos relatórios anuais."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Chiffres clés */}
 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {indicators.map((i, k) => (
 <StaggerItem key={k} className="bg-nanda-bg p-10 md:p-12" data-testid={`impact-${k}`}>
 <div className="font-serif text-6xl md:text-7xl text-nanda-ink num-editorial">{i.value}</div>
 <div className="label-overline text-nanda-muted mt-5">{i.label}</div>
 <p className="mt-5 text-nanda-inkSoft text-sm leading-relaxed max-w-xs">{i.detail}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </section>

 {/* Étape 6. Trois axes d'impact */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt" data-testid="impact-axes">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{l("Nos trois axes", "Our three axes", "Os nossos três eixos")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l("Économique. Social. Environnemental.", "Economic. Social. Environmental.", "Económico. Social. Ambiental.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {axes.map((axis, i) => {
 const Icon = axis.icon;
 return (
 <StaggerItem key={i} className="bg-nanda-bg p-10 md:p-12" data-testid={`impact-axis-${i}`}>
 <div className="flex items-start justify-between mb-8">
 <div className="text-nanda-terracotta">
 <Icon size={32} strokeWidth={1.2} />
 </div>
 <div className="label-overline text-nanda-muted num-editorial">0{i + 1}</div>
 </div>
 <div className="label-overline text-nanda-muted mb-3">{axis.label}</div>
 <h3 className="font-serif text-2xl md:text-3xl leading-tight text-nanda-ink mb-8">{axis.title}</h3>
 <div className="rule-gold w-16 mb-8" />
 <ul className="space-y-4">
 {axis.points.map((p, k) => (
 <li key={k} className="flex gap-3 text-nanda-inkSoft text-[15px] leading-relaxed">
 <span className="h-px w-4 bg-nanda-ink shrink-0 mt-3" />
 <span>{p}</span>
 </li>
 ))}
 </ul>
 </StaggerItem>
 );
 })}
 </Stagger>
 </div>
 </section>

 {/* Méthode */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-6">{l("Notre méthode", "Our method", "O nosso método")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-12">
 {l("L'impact se mesure dans le temps long.", "Impact is measured over the long term.", "O impacto mede-se a longo prazo.")}
 </h2>
 <div className="space-y-6 text-nanda-inkSoft text-base md:text-[17px] max-w-2xl leading-relaxed">
 <p>{l(
 "Nous préférons publier des chiffres vérifiables plutôt que des slogans. Les indicateurs de cette page seront enrichis au fil de nos bilans annuels et de nos rapports d'impact.",
 "We prefer to publish verifiable figures rather than slogans. The indicators on this page will be enriched throughout our annual reports and impact reports.",
 "Preferimos publicar números verificáveis em vez de slogans. Os indicadores desta página serão enriquecidos ao longo dos nossos relatórios anuais e relatórios de impacto."
 )}</p>
 <p>{l(
 "Pour toute demande de précisions ou pour recevoir notre rapport d'impact annuel, notre équipe reste disponible.",
 "For any request for clarification or to receive our annual impact report, our team remains available.",
 "Para qualquer pedido de esclarecimento ou para receber o nosso relatório de impacto anual, a nossa equipa está disponível."
 )}</p>
 </div>
 <Link
 to="/contact?subject=Institutionnel"
 data-testid="impact-cta"
 className="btn-arrow mt-12 inline-flex items-center gap-3 label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500"
 >
 {l("Demander notre rapport d'impact", "Request our impact report", "Solicitar o nosso relatório de impacto")}
 <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
